import {
  AvailableTicketInfo,
  Event,
  EventType,
  TicketPurchaseInput,
} from '@graphql/__generated/graphql'

export enum CONFIG_OPTIONS {
  SHOW_FULL_REGISTRATION_OPTIONS,
  SHOW_ACCOUNT_PROMPT,
  SHOW_VISIBLE_REGISTRATION_OPT_IN,
  SHOW_ATTENDEE_BOX,
  ALLOW_DIRECT_DEBIT,
  SHOW_GOOGLE_ADS,
  SHOW_QUICK_REGISTRATION_OPTIONS,
}

export const QUICK_REGISTRATION_EVENT_IDS = [
  '31fffe01-557f-4c3c-a7f3-cb1ec9772277',
  '0ca669ca-b167-4f69-a211-6817ad502349',
  'ed27bac1-0db6-46b0-a4b3-51f4a0993cb5',
  'e43c495e-1ebf-4bd0-8902-d524f26c0d1a',
  '4f25f4db-9795-44b7-b639-5ede8cb8c4fc',
]

export const VAT_EXCLUDED_ORGANISATIONS = [
  'Sales Geek Essex',
  'Sales Geek Scotland',
  'The LSP Compliance Group',
  'egg',
  'Imaginary Friends',
  'Simaril Events',
  'Silmaril Events',
]

const DEFAULT_REGISTRATION_VIDEO_URL =
  'https://forumm-images-prod.s3.eu-west-1.amazonaws.com/user-content/manual-upload/video-background.mp4'

const EVENT_REGISTRATION_VIDEO_URLS: Record<string, string> = {
  'c987cfd9-3732-4d21-a586-293ed6d36784':
    'https://forumm-images-prod.s3.eu-west-1.amazonaws.com/user-content/manual-upload/video-background_CE.mp4',
  'c2cba07a-a403-440c-8f0e-6f0788ad907b':
    'https://forumm-images-prod.s3.eu-west-1.amazonaws.com/user-content/manual-upload/Infinite+Intentions+Video+(1).mp4',
  'acc6d01b-d339-4dfc-9df3-8ccef3ec7594':
    'https://forumm-images-prod.s3.eu-west-1.amazonaws.com/user-content/manual-upload/The+Scottish+Marketing+Network+Highlight+Reel.mp4',
  'deef2f23-a324-42bb-8cac-e632e135faee':
    'https://forumm-images-prod.s3.eu-west-1.amazonaws.com/user-content/manual-upload/Hampden+Park+Banner.jpg',
  '795fdc4b-10fa-4f16-8be6-7046e1b3a6bc':
    'https://forumm-images-prod.s3.eu-west-1.amazonaws.com/user-content/manual-upload/Hampden+Park+Banner.jpg',
  '91947a10-235c-4e1e-94bd-442257efec8d':
    'https://forumm-images-prod.s3.eu-west-1.amazonaws.com/user-content/manual-upload/Celtic+Park+Background.png',
}

export const resolveRegistrationEventId = (
  eventIdQuery: string | string[] | undefined,
  eventIdsMap: Record<string, string>
): string | undefined => {
  const routeEventId = Array.isArray(eventIdQuery)
    ? eventIdQuery[0]
    : eventIdQuery

  if (!routeEventId) return routeEventId

  const decodedEventId = decodeURIComponent(routeEventId).trim().toLowerCase()

  if (eventIdsMap[routeEventId]) return eventIdsMap[routeEventId]
  if (eventIdsMap[decodedEventId]) return eventIdsMap[decodedEventId]

  return routeEventId
}

export const getRegistrationVideoUrl = (eventId?: string): string => {
  if (!eventId) return DEFAULT_REGISTRATION_VIDEO_URL

  return (
    EVENT_REGISTRATION_VIDEO_URLS[eventId] ?? DEFAULT_REGISTRATION_VIDEO_URL
  )
}

export const isVatIncludedForOrganisation = (
  organisationName?: string
): boolean => {
  return !VAT_EXCLUDED_ORGANISATIONS.includes(organisationName ?? '')
}

export const getAdditionalTicketsForCount = ({
  currentTickets,
  requestedCount,
  enabled,
  selectedTicketName,
  defaultTicketTitle,
}: {
  currentTickets: TicketPurchaseInput[]
  requestedCount: number
  enabled: boolean
  selectedTicketName?: string
  defaultTicketTitle?: string
}): TicketPurchaseInput[] => {
  if (!enabled) return []

  if (currentTickets.length > requestedCount) {
    return currentTickets.slice(0, requestedCount)
  }

  let additionalTickets = [...currentTickets]
  while (additionalTickets.length < requestedCount) {
    additionalTickets = [
      ...additionalTickets,
      {
        title: selectedTicketName ?? defaultTicketTitle!,
        fullName: '',
      },
    ]
  }

  return additionalTickets
}

export const shouldShowDiscountCode = ({
  availableTickets,
  discountPercentage,
  discountAmount,
  ticketTotalPrice,
}: {
  availableTickets: Pick<AvailableTicketInfo, 'discountCodes'>[]
  discountPercentage?: number
  discountAmount?: number
  ticketTotalPrice?: number
}): boolean => {
  const hasPrice =
    (ticketTotalPrice ?? 0) > 0 ||
    discountPercentage === 100 ||
    (discountAmount ?? 0) > 0
  const discountsRemaining = availableTickets.reduce((total, ticket) => {
    const availableDiscount = ticket.discountCodes?.reduce(
      (count, discount) => {
        if (discount.remaining && discount.remaining > 0) {
          return count + discount.remaining
        }

        return count
      },
      0
    )

    return total + (availableDiscount ?? 0)
  }, 0)

  return hasPrice && discountsRemaining > 0
}

export const areAllTicketRegistrationFieldsFilled = (
  fieldVals: Record<string, any>[],
  totalTickets: number,
  fieldsToValidate: string[]
): boolean => {
  if (fieldsToValidate.length === 0) return true
  if (!Array.isArray(fieldVals)) return false
  if (fieldVals.length !== totalTickets) return false

  return fieldVals.every((row) => {
    return fieldsToValidate.every((field) => {
      if (typeof row[field] === 'boolean') return row[field]
      if (typeof row[field] === 'string') return row[field].trim() !== ''
      return !!row[field]
    })
  })
}

const getRegistrationFieldErrorKey = (
  attendeeIndex: number,
  fieldName: string
): string => `${attendeeIndex}::${fieldName.replaceAll(' ', '_').toLowerCase()}`

const compileRegistrationAnswers = (
  answers: { name: string; value: string }[][]
): Record<string, string[]> => {
  const sortedAnswers: Record<string, string[]> = {}

  answers.forEach((registrationAnswers) => {
    registrationAnswers.forEach((answer) => {
      if (!!answer.value)
        !!sortedAnswers[answer.name]
          ? sortedAnswers[answer.name].push(answer.value)
          : (sortedAnswers[answer.name] = [answer.value])
    })
  })

  return sortedAnswers
}

export const getRegistrationFieldErrors = ({
  event,
  usersData,
  fieldVals,
  selectedAttendeeIndex,
}: {
  event: Partial<Event>
  usersData: any[]
  fieldVals: any[]
  selectedAttendeeIndex?: number
}): Record<string, string | undefined> => {
  const payload: Record<string, string | undefined> = {}

  const uniqueRegFields = event?.registrationFields
    ?.filter((registrationField) => !!registrationField.unique)
    .map((registrationField) => registrationField.name)

  const requiredRegFields = event?.registrationFields
    ?.filter((registrationField) => !!registrationField.required)
    .map((registrationField) => registrationField.name)

  const regAnswers = usersData.map((fieldValue) => {
    return Object.keys(fieldValue ?? {}).map((key) => ({
      name: key,
      value: fieldValue[key],
    }))
  })

  if (uniqueRegFields?.length === 0 && requiredRegFields?.length === 0) {
    return payload
  }

  requiredRegFields?.forEach((requiredFieldName) => {
    if (
      !!fieldVals &&
      fieldVals.length > 0 &&
      fieldVals.length > (selectedAttendeeIndex ?? 0)
    ) {
      fieldVals
        .filter((_, index) => {
          if (!selectedAttendeeIndex) return true
          if (selectedAttendeeIndex === index) return true
          return false
        })
        .forEach((fieldValue, index) => {
          const attendeeIndex = selectedAttendeeIndex ?? index
          if (!fieldValue?.[requiredFieldName]) {
            payload[
              getRegistrationFieldErrorKey(attendeeIndex, requiredFieldName)
            ] = `This answer is required to move forward.`
          }
        })
    } else {
      payload[
        getRegistrationFieldErrorKey(
          selectedAttendeeIndex ?? 0,
          requiredFieldName
        )
      ] = `This answer is required to move forward.`
    }
  })

  uniqueRegFields?.forEach((uniqueFieldName) => {
    if (!!fieldVals && fieldVals.length > 0) {
      fieldVals.forEach((fieldValue, index) => {
        const fieldValueAnswers: { name: string; value: string }[][] = []

        fieldVals.forEach((otherFieldValue, otherIndex) => {
          if (otherIndex !== index)
            fieldValueAnswers.push(
              Object.keys(otherFieldValue ?? {}).map((key) => ({
                name: key,
                value: otherFieldValue[key],
              }))
            )
        })

        const sortedAnswers = compileRegistrationAnswers([
          ...regAnswers,
          ...fieldValueAnswers,
        ])

        if (!!sortedAnswers?.[uniqueFieldName]?.length) {
          if (
            fieldValue?.[uniqueFieldName] &&
            sortedAnswers[uniqueFieldName].includes(fieldValue[uniqueFieldName])
          ) {
            payload[getRegistrationFieldErrorKey(index, uniqueFieldName)] =
              `This answer has already been given by another attendee.`
          }
        }
      })
    }
  })

  return payload
}

// NOTE: Temporary config options
export const getRegistrationConfig = (
  opt: CONFIG_OPTIONS,
  event: Partial<Event> | undefined
): boolean => {
  // console.log(`[RegistrationConfig]: `, { opt, event })
  if (!event) return true
  const isInPerson = event.event?.eventType === EventType.InPerson
  // const isOnline = event.event?.eventType === EventType.Online

  const orgName = event?.event?.organizationName?.toLowerCase() ?? ''
  switch (opt) {
    case CONFIG_OPTIONS.SHOW_ACCOUNT_PROMPT:
      if (orgName.includes('sussex')) return false
      if (orgName.includes('448 studio')) return false
      break

    case CONFIG_OPTIONS.SHOW_FULL_REGISTRATION_OPTIONS:
      if (orgName.includes('sussex')) return false
      if (orgName.includes('448 studio')) return false
      break

    case CONFIG_OPTIONS.SHOW_VISIBLE_REGISTRATION_OPT_IN:
      if (orgName.includes('sussex')) return true
      if (orgName.includes('forumm')) return true
      // if (orgName.includes('imaginary friends')) return true
      if (event.eventId === '16caaaed-dc0d-44d7-936d-0a590dfef429') return true
      if (event.eventId === '31fffe01-557f-4c3c-a7f3-cb1ec9772277') return true
      return false

    case CONFIG_OPTIONS.ALLOW_DIRECT_DEBIT:
      if (orgName.includes('sussex')) return true
      if (orgName.includes('448 studio')) return true
      if (orgName.includes('forumm')) return true
      return false

    case CONFIG_OPTIONS.SHOW_ATTENDEE_BOX:
      if (orgName.includes('sussex')) return true
      // if (orgName.includes('imaginary friends')) return true
      if (orgName.includes('university of glasgow') && isInPerson) return true
      if (event.eventId === '16caaaed-dc0d-44d7-936d-0a590dfef429') return true
      if (event.eventId === 'e172670e-a062-4f05-9e33-b334a57fff93') return true
      return false

    case CONFIG_OPTIONS.SHOW_QUICK_REGISTRATION_OPTIONS:
      if (event.eventId === '236e01cf-bcfb-41e0-94e9-4c173e2e16f4') return false
      if (event.eventId === '59080128-59d6-4164-a824-0e48b01ef75f') return false
      return true
  }
  return true
}

export const getOrganisationConfig = (
  opt: CONFIG_OPTIONS,
  orgName: string | undefined
): boolean => {
  // console.log(`[OrganisationConfig]: `, { opt, orgName })
  if (!orgName) return true
  const sanitisedOrgName = orgName.toLowerCase()

  switch (opt) {
    case CONFIG_OPTIONS.SHOW_GOOGLE_ADS:
      if (sanitisedOrgName.includes('sussex')) return false
      return true
  }
  return true
}
